import { get } from 'lodash';
import { logger } from 'client/utils/logger/logger';
import { EdmundsAPI } from 'client/data/api/api-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { VisitorModel } from 'client/data/models/visitor';
import { buildStyleIdsShotTypePhotosPathsWithPageSize, PhotosModel } from 'client/data/models/photos';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { ENGINE_TYPES } from 'site-modules/shared/constants/lease-deals/engine-types';

const ALL_TYPES = 'all';

const getLeaseDealsPath = () => 'leaseDealsData';
const getPhotosPath = width => `photos.width["${width}"]`;
const getFirstBestPaymentDealMMYPath = ({ make, model, year }) => `leaseDeals["${make}"]["${model}"]["${year}"].first`;
const getLeaseDealsBodyEngineTypePath = ({ bodyType = ALL_TYPES, engineType = ALL_TYPES }) =>
  `leaseDeals["${bodyType}"]["${engineType}"]`;

export const LeaseDealsPaths = {
  getLeaseDealsPath,
  getPhotosPath,
  getFirstBestPaymentDealMMYPath,
  getLeaseDealsBodyEngineTypePath,
};

const LEASE_SPECIALS_URL = '/purchasefunnel/leasespecials/';

const getLeaseDealsQueryParams = params => {
  const fallbackParams = {
    paymentRatio: '*-0.007',
    sortBy: 'bodyType,paymentRatio',
    distinct: true,
    pageSize: 40,
  };

  return objectToQueryString({
    ...fallbackParams,
    ...params,
  });
};

export const LeaseDealsModel = createModelSegment('leaseDeals', [
  {
    /**
      @see getLeaseDealsPath
    */
    path: 'leaseDealsData',
    async resolve(match, context) {
      const { zipCode, state } = await context.resolveValue('location', VisitorModel);
      const url = `${LEASE_SPECIALS_URL}?${getLeaseDealsQueryParams({ state, zip: zipCode })}`;

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url)
        .catch(err => {
          logger.warn(`Failed to load data - ${err}`);
          return {};
        });
    },
  },
  {
    /**
      @see getPhotosPath
    */
    path: 'photos.width["{width}"]',
    async resolve(match, context) {
      const leaseDealsData = await context.resolveValue(getLeaseDealsPath());

      const styleIds = leaseDealsData.deals?.map(({ styleId }) => styleId).join(',');
      if (!styleIds) return [];

      try {
        const response = await context.resolveValue(
          buildStyleIdsShotTypePhotosPathsWithPageSize(styleIds, 'OEM', 'FQ', match.width, 40),
          PhotosModel
        );

        return get(response, 'photos', []).map(photo => ({
          href: get(photo, 'sources[0].link.href', null),
          title: get(photo, 'title', null),
          styleIds: get(photo, 'styleIds', []).reduce(
            (styleIdsObj, styleId) => ({ ...styleIdsObj, [+styleId]: true }),
            {}
          ),
        }));
      } catch (e) {
        logger.warn('Failed to load data', e);
        return [];
      }
    },
  },
  {
    /**
     * This path is used for the entry point (link) component, which redirects a user to hidden gems lease deals page
     * @see getFirstBestPaymentDealMMYPath
     */
    path: 'leaseDeals["{make}"]["{model}"]["{year}"].first',
    async resolve(match, context) {
      const { make, model, year } = match;
      const { state } = await context.resolveValue('location', VisitorModel);

      const url = `${LEASE_SPECIALS_URL}?${getLeaseDealsQueryParams({
        state,
        make,
        model,
        year,
        sortBy: 'paymentRatio',
        pageSize: 1,
      })}`;

      const response = await withMetrics(EdmundsAPI, context).fetchJson(url);

      return response.deals?.[0];
    },
  },
  {
    /**
     * This path is used for the entry point (link) component on the type page, which redirects a user to hidden gems lease deals page
     * @see getLeaseDealsBodyEngineTypePath
     */
    path: 'leaseDeals["{bodyType}"]["{engineType}"]',
    async resolve(match, context) {
      const { engineType, bodyType } = match;
      const { state } = await context.resolveValue('location', VisitorModel);

      function mapEngineTypeToExplicitValues(implicitEngineType) {
        const engineTypeMap = {
          [ENGINE_TYPES.HYBRID]: [ENGINE_TYPES.HYBRID, ENGINE_TYPES.MILD_HYBRID, ENGINE_TYPES.PLUG_IN_HYBRID].join(','),
        };

        return engineTypeMap?.[implicitEngineType] ?? implicitEngineType;
      }

      const url = `${LEASE_SPECIALS_URL}?${getLeaseDealsQueryParams({
        state,
        sortBy: 'paymentRatio',
        bodyType: bodyType !== ALL_TYPES ? bodyType : undefined,
        engineType: engineType !== ALL_TYPES ? mapEngineTypeToExplicitValues(engineType.toLowerCase()) : undefined,
      })}`;

      return withMetrics(EdmundsAPI, context).fetchJson(url);
    },
  },
]);
